import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { trackLeadContactSubmit } from '../lib/metrikaGoals';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Phone, Mail, Calculator } from 'lucide-react';
import { calculationAPI } from '../lib/api';
import DdgCaptcha from './DdgCaptcha';
import { DDG_CAPTCHA_REQUIRED_MESSAGE } from '../lib/ddgCaptcha';
import PersonalDataConsent from './PersonalDataConsent';
import MarketingConsent from './MarketingConsent';
import {
  optionalQuestionSchema,
  QUESTION_LABEL,
  WORK_EMAIL_LABEL,
} from '../lib/formFieldSchemas';

const phoneRegex = /^(\+7|7|8)?[\s-]?\(?\d{3}\)?[\s-]?\d{3}[\s-]?\d{2}[\s-]?\d{2}$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const requestCalculationSchema = z
  .object({
    contactType: z.enum(['phone', 'email'], {
      required_error: 'Выберите способ связи',
    }),
    phone: z.string().optional(),
    email: z.string().optional(),
    taskDescription: z.string().optional(),
    consent: z
      .boolean()
      .refine((val) => val === true, {
        message: 'Необходимо согласие на обработку персональных данных',
      }),
    marketingConsent: z.boolean().optional(),
    question: optionalQuestionSchema,
  })
  .refine(
    (data) => {
      if (data.contactType === 'phone') {
        return data.phone && phoneRegex.test(data.phone);
      }
      return data.email && emailRegex.test(data.email);
    },
    {
      message: 'Заполните выбранное поле корректно',
      path: ['phone'],
    },
  )
  .refine(
    (data) => {
      if (data.contactType === 'email') {
        return data.email && emailRegex.test(data.email);
      }
      return true;
    },
    {
      message: 'Введите корректный email',
      path: ['email'],
    },
  );

const buildRequestCalculationSchema = (showTaskDescription) => {
  if (!showTaskDescription) {
    return requestCalculationSchema;
  }

  return requestCalculationSchema.refine(
    (data) => Boolean(data.taskDescription?.trim()),
    {
      message: 'Опишите задачу',
      path: ['taskDescription'],
    },
  );
};

const RequestCalculationForm = ({
  open,
  onOpenChange,
  variant = 'dialog',
  privacyPolicyLink: _privacyPolicyLink,
  calculatorData = null,
  showTaskDescription = false,
  hideHeader = false,
  title = 'Обсудить задачу',
  description = 'Выберите способ связи, и мы обсудим задачу и подберём решение',
  submitLabel = 'Отправить запрос',
  onSuccess,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [contactType, setContactType] = useState('phone');

  const formSchema = useMemo(
    () => buildRequestCalculationSchema(showTaskDescription),
    [showTaskDescription],
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      contactType: 'phone',
      phone: '',
      email: '',
      taskDescription: '',
      consent: false,
      marketingConsent: false,
      question: '',
    },
  });

  useEffect(() => {
    if (variant === 'inline') return;
    if (!open) return;

    form.reset({
      contactType: 'phone',
      phone: '',
      email: '',
      taskDescription: '',
      consent: false,
      marketingConsent: false,
      question: '',
    });
    setContactType('phone');
    setIsSubmitted(false);
  }, [open, form, variant]);

  useEffect(() => {
    if (variant !== 'inline' && !open) setCaptchaToken('');
  }, [open, variant]);

  useEffect(() => {
    form.setValue('contactType', contactType);
    if (contactType === 'phone') {
      form.setValue('email', '');
    } else {
      form.setValue('phone', '');
    }
  }, [contactType, form]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const contact = data.contactType === 'phone' ? data.phone : data.email;
      const payloadCalculatorData = {
        ...(calculatorData || {}),
        ...(showTaskDescription && data.taskDescription?.trim()
          ? { taskDescription: data.taskDescription.trim() }
          : {}),
      };
      if (!captchaToken) {
        alert(DDG_CAPTCHA_REQUIRED_MESSAGE);
        return;
      }
      await calculationAPI.createCalculationRequest(
        contact,
        data.contactType,
        Object.keys(payloadCalculatorData).length > 0 ? payloadCalculatorData : null,
        undefined,
        window.location.href,
        data.consent,
        data.marketingConsent,
        data.question?.trim() || null,
        captchaToken,
      );

      trackLeadContactSubmit(calculatorData);

      if (variant === 'inline') {
        setIsSubmitted(true);
        form.reset();
        onSuccess?.();
        return;
      }

      alert('Спасибо! Мы свяжемся с вами в ближайшее время и отправим расчёт.');
      form.reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error submitting calculation request:', error);
      alert(error.message || 'Произошла ошибка. Попробуйте позже.');
      captchaRef.current?.reset();
      setCaptchaToken('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasCalculatorSummary = Boolean(
    calculatorData &&
      (
        calculatorData.userCount ||
        calculatorData.selectedServices?.length ||
        calculatorData.totalPrice ||
        calculatorData.monthlyPrice ||
        calculatorData.oneTimePrice ||
        calculatorData.productName
      ),
  );

  const calculatorSummary =
    hasCalculatorSummary && (
      <div className="mb-4 rounded-lg border border-accent/20 bg-accent/5 p-4">
        <h4 className="mb-2 text-sm font-semibold">Детали заказа:</h4>
        <div className="space-y-1 text-sm text-muted-foreground">
          <div>Количество пользователей: {calculatorData.userCount}</div>
          {calculatorData.selectedServices?.length > 0 && (
            <div>
              <div className="mb-1 font-medium">Выбранные сервисы:</div>
              <ul className="ml-2 list-inside list-disc">
                {calculatorData.selectedServices.map((service, idx) => (
                  <li key={idx}>{service.name}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-2 border-t border-border/50 pt-2 font-semibold text-foreground">
            Итого: {calculatorData.totalPrice?.toLocaleString('ru-RU')} ₽
            {calculatorData.monthlyPrice > 0 && calculatorData.oneTimePrice > 0 && ' (первый месяц)'}
            {calculatorData.monthlyPrice > 0 && calculatorData.oneTimePrice === 0 && '/мес'}
          </div>
        </div>
      </div>
    );

  const formFields = (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="contactType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Способ связи</FormLabel>
              <FormControl>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={contactType === 'phone' ? 'default' : 'outline'}
                    className={`flex-1 ${
                      contactType === 'phone'
                        ? 'bg-accent hover:bg-accent/90 text-accent-foreground'
                        : ''
                    }`}
                    onClick={() => {
                      setContactType('phone');
                      field.onChange('phone');
                    }}
                  >
                    <Phone className="mr-2 h-4 w-4" />
                    Телефон
                  </Button>
                  <Button
                    type="button"
                    variant={contactType === 'email' ? 'default' : 'outline'}
                    className={`flex-1 ${
                      contactType === 'email'
                        ? 'bg-accent hover:bg-accent/90 text-accent-foreground'
                        : ''
                    }`}
                    onClick={() => {
                      setContactType('email');
                      field.onChange('email');
                    }}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Email
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {contactType === 'phone' && (
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Номер телефона</FormLabel>
                <FormControl>
                  <Input placeholder="+7 (999) 123-45-67" type="tel" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {contactType === 'email' && (
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{WORK_EMAIL_LABEL} *</FormLabel>
                <FormControl>
                  <Input placeholder="example@company.ru" type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {showTaskDescription && (
          <FormField
            control={form.control}
            name="taskDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Описать задачу</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Например: Midjourney и Runway для команды из 8 человек, оплата со счёта компании"
                    className="min-h-[100px] resize-y"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="question"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{QUESTION_LABEL}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Кратко опишите задачу или вопрос"
                  className="min-h-[88px] resize-y"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="consent"
          render={({ field }) => (
            <FormItem>
              <PersonalDataConsent
                id="calculation-pd-consent"
                checked={field.value}
                onCheckedChange={field.onChange}
                error={form.formState.errors.consent?.message}
              />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="marketingConsent"
          render={({ field }) => (
            <FormItem>
              <MarketingConsent
                id="calculation-marketing-consent"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormItem>
          )}
        />

        <DdgCaptcha ref={captchaRef} onToken={setCaptchaToken} />

        <Button
          type="submit"
          className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
          disabled={isSubmitting || !captchaToken}
        >
          {isSubmitting ? 'Отправка...' : submitLabel}
        </Button>
      </form>
    </Form>
  );

  if (variant === 'inline') {
    if (isSubmitted) {
      return (
        <div
          className="rounded-[14px] border border-[#e5e7eb] bg-white p-8 text-center shadow-sm"
          data-testid="discuss-task-form-success"
        >
          <h2 className="text-2xl font-bold text-[#101828]">Заявка отправлена</h2>
          <p className="mt-4 text-base text-[#62748E]">
            Спасибо! Мы свяжемся с вами в ближайшее время.
          </p>
        </div>
      );
    }

    return (
      <div
        className="rounded-[14px] border border-[#e5e7eb] bg-white p-8 shadow-sm"
        data-testid="discuss-task-form"
      >
        {!hideHeader && (
          <div className="mb-6">
            <h2 className="flex items-center gap-2 text-2xl font-bold text-[#101828]">
              <Calculator className="h-6 w-6 text-accent" />
              {title}
            </h2>
            <p className="mt-2 text-base text-[#62748E]">{description}</p>
          </div>
        )}
        {calculatorSummary}
        {formFields}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[min(90dvh,100%)] overflow-y-auto top-[max(4.5rem,env(safe-area-inset-top))] translate-y-0 sm:top-[50%] sm:max-w-[500px] sm:translate-y-[-50%]">
        <DialogHeader className="pr-10">
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-accent" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {calculatorSummary}
        {formFields}
      </DialogContent>
    </Dialog>
  );
};

export default RequestCalculationForm;
